// React-Flow island — hooks permitted per CLAUDE.md island exception
// (extended from the lineage canvas to the RI read-only renderer). Read-only.
//
// Flip-card node modelled on the lineage Database node (AppNode): front face shows
// a summary; back face lists FK/PK columns with per-column connection handles. The
// flip is driven globally by the "Column detail" toggle (data.expanded), exactly like
// lineage flips all nodes on the System↔Data switch. The two-phase rotate
// (0→90, swap face while edge-on, -90→0) lets the two faces differ in height.
import React, {useState, useEffect, useRef} from 'react';
import {Handle, Position, useUpdateNodeInternals} from '@xyflow/react';

const HALF = 200;

function typeIcon(t) {
  return t === 'view' ? '▷' : '▦';
}

function colMarker(c) {
  if (c.isPk) return {sym: '▪', cls: 'ri-col-pk'};
  if (c.isFk || c.connected) return {sym: '●', cls: 'ri-col-fk'};
  return {sym: '·', cls: 'ri-col-other'};
}

function FrontFace({data}) {
  const total = data.colTotal;
  const fkCount = (data.columns || []).filter((c) => c.isFk).length;
  const isCircular = (data.badges || []).includes('CIRCULAR');
  return (
    <div className="ri-face ri-face-front">
      <div className="ri-node-header">
        <span className="ri-node-icon">{typeIcon(data.objectType)}</span>
        <span className="ri-node-label" title={data.label}>{data.label}</span>
        <span className="ri-node-type-chip">{data.objectType || 'table'}</span>
      </div>
      {(total != null || isCircular) && (
        <div className="ri-node-summary">
          {total != null && (
            <span>{`${total} column${total === 1 ? '' : 's'}`}{fkCount ? ` · ${fkCount} FK` : ''}</span>
          )}
          {isCircular && <span className="ri-type-badge ri-badge-circular">CIRCULAR</span>}
        </div>
      )}
    </div>
  );
}

function BackFace({data, nodeId}) {
  const cols = data.columns || [];
  return (
    <div className="ri-face ri-face-back">
      <div className="ri-node-header">
        <span className="ri-node-icon">{typeIcon(data.objectType)}</span>
        <span className="ri-node-label" title={data.label}>{data.label}</span>
      </div>
      <div className="ri-node-columns">
        {cols.map((c) => {
          const m = colMarker(c);
          const withHandles = c.isFk || c.isPk || c.connected;
          const isSel = data.selectedColumnId === c.columnId;
          const isHl = data.columnSelectionActive && data.highlightedColumns && data.highlightedColumns.has(c.columnId);
          const isDim = data.columnSelectionActive && !isHl && !isSel;
          const rowCls = [
            'ri-node-col-row', m.cls,
            withHandles ? 'ri-col-clickable' : '',
            isSel ? 'ri-col-selected' : '',
            isHl && !isSel ? 'ri-col-highlighted' : '',
            isDim ? 'ri-col-dimmed' : '',
          ].filter(Boolean).join(' ');
          return (
            <div
              key={c.columnId || c.name}
              className={rowCls}
              onClick={withHandles ? (e) => {
                e.stopPropagation();
                if (data.onColumnClick) data.onColumnClick(nodeId, c.columnId);
              } : undefined}
            >
              {/* Absolute at the node's left/right edge, vertically centred on this row
                  (the row is position:relative), so edges attach cleanly at the column
                  instead of a point inset behind the node body. */}
              {withHandles && (
                <Handle
                  id={`col-${c.columnId}-target`}
                  type="target"
                  position={Position.Left}
                  isConnectable={false}
                  className="ri-col-handle"
                />
              )}
              <span className="ri-col-marker">{m.sym}</span>
              <span className="ri-col-name" title={c.name}>{c.name}</span>
              {c.dataType && <span className="ri-col-type">{c.dataType}</span>}
              {withHandles && (
                <Handle
                  id={`col-${c.columnId}-source`}
                  type="source"
                  position={Position.Right}
                  isConnectable={false}
                  className="ri-col-handle"
                />
              )}
            </div>
          );
        })}
        {data.moreCount > 0 && (
          <div
            className="ri-node-more"
            data-testid="ri-node-expand"
            onClick={(e) => {
              e.stopPropagation();
              if (data.onExpandMore) data.onExpandMore(nodeId);
            }}
          >
            ＋ {data.moreCount} more column{data.moreCount === 1 ? '' : 's'}
          </div>
        )}
      </div>
    </div>
  );
}

function RiColumnNode({id, data, selected}) {
  const updateNodeInternals = useUpdateNodeInternals();
  const targetFace = data.expanded ? 'back' : 'front';
  const [face, setFace] = useState(targetFace);
  const [rot, setRot] = useState(0);
  const timers = useRef([]);

  useEffect(() => {
    if (face === targetFace) return undefined;
    setRot(90);
    const t1 = setTimeout(() => {
      setFace(targetFace);
      setRot(-90);
      const t2 = setTimeout(() => setRot(0), 20);
      timers.current.push(t2);
    }, HALF);
    timers.current.push(t1);
    return () => timers.current.splice(0).forEach(clearTimeout);
  }, [targetFace, face]);

  // Re-measure handles whenever the visible face (and thus its handle set) changes,
  // so edges reconnect to node-level vs column-level handles correctly.
  useEffect(() => {
    updateNodeInternals(id);
  }, [face, id, updateNodeInternals, data.columns]);

  const cls = [
    'ri-node',
    data.objectType === 'view' ? 'ri-node-view' : 'ri-node-table',
    selected ? 'ri-node-selected' : '',
    data.highlighted ? 'ri-node-highlighted' : '',
    data.dimmed ? 'ri-node-dimmed' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={cls}
      data-testid="ri-node"
      style={{transform: `perspective(900px) rotateY(${rot}deg)`, transition: 'transform 0.2s ease'}}
    >
      {/* Node-level handles — used for object-level edges and view-dependency edges. */}
      <Handle id="node-target" type="target" position={Position.Left} isConnectable={false} className="ri-node-handle" />
      <Handle id="node-source" type="source" position={Position.Right} isConnectable={false} className="ri-node-handle" />
      {face === 'front' ? <FrontFace data={data} /> : <BackFace data={data} nodeId={id} />}
    </div>
  );
}

export default RiColumnNode;
